import { useEffect, useState } from 'react';
import { TourBagStaticService } from '@/service';
import { HandHelper, LoftLieHelper, PutterHelper, ShaftLengthHelper, SwingWeightHelper } from '@/library';
import * as APIModel from '@/api/models';

const PUTTER_HEAD_TYPE_ID = 6;

export interface IPutterStaticDropdownData {
  putterLevel: PutterHelper.IStaticDropdownData<APIModel.PutterLevel>;
  putterType: PutterHelper.IStaticDropdownData<APIModel.PutterType>;
  putterWeight: PutterHelper.IStaticDropdownData<APIModel.PutterWeight>;
}

export interface IPutterDynamicDropdownData {
  putterFace: PutterHelper.IDynamicDropdownData<APIModel.PutterFaceForSaveDto>;
  putterGrip: PutterHelper.IDynamicDropdownData<APIModel.PutterGripForSaveDto>;
  putterHeadFinish: PutterHelper.IDynamicDropdownData<APIModel.HeadFinishForSaveDto>;
  putterHeadModel: PutterHelper.IDynamicDropdownData<APIModel.PutterHeadModelForSaveDto>;
  putterNeck: PutterHelper.IDynamicDropdownData<APIModel.PutterNeckForSaveDto>;
  putterShaft: PutterHelper.IDynamicDropdownData<APIModel.PutterShaftForSaveDto>;
  putterSightline: PutterHelper.IDynamicDropdownData<APIModel.PutterSightLineForSaveDto>;
}

export const useEditPutter = () => {
  const [handDropdownData, setHandDropdownData] = useState<HandHelper.IHandOptionMap>();
  const [loftDropdownData, setLoftDropdownData] = useState<LoftLieHelper.ILoftOptionMap>();
  const [lieDropdownData, setLieDropdownData] = useState<LoftLieHelper.ILieOptionMap>();
  const [putterStaticDropdownData, setPutterStaticDropdownData] = useState<IPutterStaticDropdownData>();
  const [putterDynamicDropdownData, setPutterDynamicDropdownData] = useState<IPutterDynamicDropdownData>();
  const [shaftLengthDropdownData, setShaftLengthDropdownData] = useState<ShaftLengthHelper.ShaftLengthOptionMap>();
  const [swingWtDropdownData, setSwingWtDropdownData] = useState<SwingWeightHelper.ISwingWtOptionMap>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getDropdownData = async () => {
      const [
        handLookup,
        digitSourceTable,
        fractionSourceTable,
        lieDigitJoinTable,
        lieFractionJoinTable,
        loftDigitJoinTable,
        loftFractionJoinTable,
        putterFace,
        putterGrip,
        putterHeadFinish,
        putterHeadModel,
        putterLevel,
        putterNeck,
        putterShaft,
        putterSightline,
        putterType,
        putterWeight,
        shaftLengthDigit,
        shaftLengthFraction,
        swingWtPrefix,
        swingWtSuffix,
      ] = await Promise.all([
        TourBagStaticService.getHandLookupData(),
        TourBagStaticService.getDigitData(),
        TourBagStaticService.getFractionData(),
        TourBagStaticService.getLieDigitsData(),
        TourBagStaticService.getLieFractionData(),
        TourBagStaticService.getLoftDigitData(),
        TourBagStaticService.getLoftFractionData(),
        TourBagStaticService.getPutterFaceData(),
        TourBagStaticService.getPutterGripData(),
        TourBagStaticService.getPutterHeadFinishData(),
        TourBagStaticService.getPutterHeadModelData(),
        TourBagStaticService.getPutterLevelData(),
        TourBagStaticService.getPutterNeckData(),
        TourBagStaticService.getPutterShaftData(),
        TourBagStaticService.getPutterSightlineData(),
        TourBagStaticService.getPutterTypeData(),
        TourBagStaticService.getPutterWeightData(),
        TourBagStaticService.getShaftLengthDigitData(),
        TourBagStaticService.getShaftLengthFractionData(),
        TourBagStaticService.getSwingWtPrefixData(),
        TourBagStaticService.getSwingWtSuffixData(),
      ]);

      // set hand dropdown data
      const handDropdownOptionMap = HandHelper.buildHandDropdown(handLookup);
      setHandDropdownData(handDropdownOptionMap);

      // build loft & lie dropdown data
      const { loftDropdownOptionMap, lieDropdownOptionMap } = LoftLieHelper.buildDropdownData({
        headTypeId: PUTTER_HEAD_TYPE_ID,
        digitSourceTable,
        fractionSourceTable,
        lieDigitJoinTable,
        lieFractionJoinTable,
        loftDigitJoinTable,
        loftFractionJoinTable,
      });
      setLoftDropdownData(loftDropdownOptionMap);
      setLieDropdownData(lieDropdownOptionMap);

      // build putter static & dynamic dropdown data
      const { putterLevelDropdownOptionMap, putterTypeDropdownOptionMap, putterWeightDropdownOptionMap } = PutterHelper.buildStaticDropdownOptionMap({ putterLevel, putterType, putterWeight });
      setPutterStaticDropdownData({ putterLevel: putterLevelDropdownOptionMap, putterType: putterTypeDropdownOptionMap, putterWeight: putterWeightDropdownOptionMap });
      const {
        putterFaceDropdownOptionMap,
        putterGripDropdownOptionMap,
        putterHeadFinishDropdownOptionMap,
        putterHeadModelDropdownOptionMap,
        putterNeckDropdownOptionMap,
        putterShaftDropdownOptionMap,
        putterSightlineDropdownOptionMap,
      } = PutterHelper.buildDynamicDropdownOptionMap({ putterFace, putterGrip, putterHeadFinish, putterHeadModel, putterNeck, putterShaft, putterSightline });
      setPutterDynamicDropdownData({
        putterFace: putterFaceDropdownOptionMap,
        putterGrip: putterGripDropdownOptionMap,
        putterHeadFinish: putterHeadFinishDropdownOptionMap,
        putterHeadModel: putterHeadModelDropdownOptionMap,
        putterNeck: putterNeckDropdownOptionMap,
        putterShaft: putterShaftDropdownOptionMap,
        putterSightline: putterSightlineDropdownOptionMap,
      });

      // build shaft length dropdown data
      const shaftLengthDropdownOptionMap = ShaftLengthHelper.buildDropdownData({
        digitJoinTable: shaftLengthDigit,
        fractionJoinTable: shaftLengthFraction,
        digitSourceTable,
        fractionSourceTable,
      });
      setShaftLengthDropdownData(shaftLengthDropdownOptionMap);

      // build swing wt dropdown data
      const swingWtDropdownOptionMap = SwingWeightHelper.buildDropdownData({ swingWtPrefix, swingWtSuffix });
      setSwingWtDropdownData(swingWtDropdownOptionMap);

      setLoading(false);
    };
    getDropdownData();
  }, []);

  return {
    handDropdownData,
    loftDropdownData,
    lieDropdownData,
    putterStaticDropdownData,
    putterDynamicDropdownData,
    shaftLengthDropdownData,
    swingWtDropdownData,
    loading,
  };
};
